import { alpha, Button, useTheme } from "@mui/material";
import PropTypes from "prop-types";

/**
 * ButtonAtom component for rendering a customizable button with enhanced features.
 *
 * @param {string} variant - The button variant ('contained', 'outlined', 'text').
 * @param {string} color - The button color ('primary', 'secondary', 'success', 'error', 'info', 'warning', 'subtle').
 * @param {function} onClick - The click event handler.
 * @param {boolean} disabled - If true, the button will be disabled.
 * @param {boolean} fullWidth - If true, the button will take the full width of its container.
 * @param {boolean} loading - If true, displays a loading state.
 * @param {string} size - Button size ('small', 'medium', 'large').
 * @param {element} loadingIndicator - Custom loading indicator component.
 * @param {string} ariaLabel - Accessibility label.
 * @param {object} sx - Additional custom styles.
 * @param {object} props - Other props to pass to the Button component.
 * @returns {JSX.Element} The enhanced Button component.
 */
const ButtonAtom = ({
  variant = "contained",
  color = "primary",
  onClick,
  disabled = false,
  fullWidth = false,
  loading = false,
  size = "medium",
  children,
  startIcon,
  endIcon,
  loadingIndicator = null,
  ariaLabel,
  sx,
  ...props
}) => {
  const theme = useTheme();

  // Define color options beyond the default MUI palette
  const getColorStyles = (colorName) => {
    // Handle subtle color for dark mode - use white/gray tones
    if (colorName === "subtle") {
      return {
        main: "rgba(255, 255, 255, 0.7)",
        dark: "rgba(255, 255, 255, 0.9)",
        light: "rgba(255, 255, 255, 0.5)",
        contrastText: "#ffffff",
      };
    }

    // Standard MUI colors
    if (theme.palette[colorName]) {
      return {
        main: theme.palette[colorName].main,
        dark: theme.palette[colorName].dark || theme.palette[colorName].main,
        light: theme.palette[colorName].light || theme.palette[colorName].main,
        contrastText: theme.palette.getContrastText
          ? theme.palette.getContrastText(theme.palette[colorName].main)
          : "#000000",
      };
    }

    // Custom colors defined elsewhere in the theme
    if (theme.customColors?.[colorName]) {
      return {
        main: theme.customColors[colorName].main,
        dark: theme.customColors[colorName].dark,
        light: theme.customColors[colorName].light,
        contrastText: theme.customColors[colorName].contrastText,
      };
    }

    // Fallback to primary if color doesn't exist
    return {
      main: theme.palette.primary.main,
      dark: theme.palette.primary.dark || theme.palette.primary.main,
      light: theme.palette.primary.light || theme.palette.primary.main,
      contrastText: theme.palette.getContrastText
        ? theme.palette.getContrastText(theme.palette.primary.main)
        : "#000000",
    };
  };

  const colorStyles = getColorStyles(color);

  // Helper function to convert rgb/rgba to hex
  const rgbToHex = (color) => {
    // If already hex, return as is
    if (color.startsWith("#")) return color;

    // Extract RGB values from rgb() or rgba()
    const match = color.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
    if (!match) return color;

    const r = parseInt(match[1]);
    const g = parseInt(match[2]);
    const b = parseInt(match[3]);

    return `#${r.toString(16).padStart(2, "0")}${g
      .toString(16)
      .padStart(2, "0")}${b.toString(16).padStart(2, "0")}`;
  };

  // Helper function to lighten a color
  const lightenColor = (color, percent = 20) => {
    try {
      // Convert to hex if needed
      const hexColor = rgbToHex(color);

      // Remove # if present
      const hex = hexColor.replace("#", "");

      // Handle short hex codes
      const fullHex =
        hex.length === 3
          ? hex
              .split("")
              .map((c) => c + c)
              .join("")
          : hex;

      // Convert to RGB
      const r = parseInt(fullHex.substring(0, 2), 16);
      const g = parseInt(fullHex.substring(2, 4), 16);
      const b = parseInt(fullHex.substring(4, 6), 16);

      // Lighten
      const newR = Math.min(255, Math.floor(r + (255 - r) * (percent / 100)));
      const newG = Math.min(255, Math.floor(g + (255 - g) * (percent / 100)));
      const newB = Math.min(255, Math.floor(b + (255 - b) * (percent / 100)));

      // Convert back to hex
      return `#${newR.toString(16).padStart(2, "0")}${newG
        .toString(16)
        .padStart(2, "0")}${newB.toString(16).padStart(2, "0")}`;
    } catch (e) {
      // Fallback to original color if conversion fails
      return color;
    }
  };

  // Helper function to darken a color
  const darkenColor = (color, percent = 15) => {
    try {
      // Convert to hex if needed
      const hexColor = rgbToHex(color);

      // Remove # if present
      const hex = hexColor.replace("#", "");

      // Handle short hex codes
      const fullHex =
        hex.length === 3
          ? hex
              .split("")
              .map((c) => c + c)
              .join("")
          : hex;

      // Convert to RGB
      const r = parseInt(fullHex.substring(0, 2), 16);
      const g = parseInt(fullHex.substring(2, 4), 16);
      const b = parseInt(fullHex.substring(4, 6), 16);

      // Darken
      const newR = Math.floor(r * (1 - percent / 100));
      const newG = Math.floor(g * (1 - percent / 100));
      const newB = Math.floor(b * (1 - percent / 100));

      // Convert back to hex
      return `#${newR.toString(16).padStart(2, "0")}${newG
        .toString(16)
        .padStart(2, "0")}${newB.toString(16).padStart(2, "0")}`;
    } catch (e) {
      // Fallback to original color if conversion fails
      return color;
    }
  };

  // Generate gradient colors
  const gradientLight = lightenColor(colorStyles.main, 20);
  const gradientDark = darkenColor(colorStyles.main, 15);

  // Size mappings with modern spacing
  const sizeStyles = {
    small: {
      padding: "8px 20px",
      fontSize: theme.typography.pxToRem(13),
      height: 36,
    },
    medium: {
      padding: "12px 24px",
      fontSize: theme.typography.pxToRem(14),
      height: 44,
    },
    large: {
      padding: "14px 32px",
      fontSize: theme.typography.pxToRem(16),
      height: 52,
    },
  };

  // Determine variant-specific styles with modern dark mode aesthetics
  const variantStyles = {
    contained: {
      background: `linear-gradient(135deg, ${colorStyles.main} 0%, ${gradientLight} 100%)`,
      color: colorStyles.contrastText,
      border: "none",
      boxShadow: `0 4px 20px ${alpha(colorStyles.main, 0.2)}`,
      "&:hover": {
        background: `linear-gradient(135deg, ${gradientDark} 0%, ${colorStyles.main} 100%)`,
        transform: "translateY(-2px)",
        boxShadow: `0 6px 28px ${alpha(colorStyles.main, 0.5)}`,
      },
      "&:active": {
        transform: "translateY(0)",
        boxShadow: `0 2px 12px ${alpha(colorStyles.main, 0.3)}`,
      },
    },
    outlined: {
      backgroundColor: "transparent",
      border: `1.5px solid ${
        color === "subtle"
          ? "rgba(255, 255, 255, 0.2)"
          : alpha(colorStyles.main, 0.5)
      }`,
      color: colorStyles.main,
      boxShadow: "none",
      "&:hover": {
        backgroundColor:
          color === "subtle"
            ? alpha("#ffffff", 0.05)
            : alpha(colorStyles.main, 0.08),
        borderColor:
          color === "subtle" ? "rgba(255, 255, 255, 0.35)" : colorStyles.main,
        transform: "translateY(-1px)",
        boxShadow:
          color === "subtle"
            ? "0 4px 16px rgba(255, 255, 255, 0.1)"
            : `0 4px 16px ${alpha(colorStyles.main, 0.2)}`,
      },
      "&:active": {
        transform: "translateY(0)",
        backgroundColor:
          color === "subtle"
            ? alpha("#ffffff", 0.08)
            : alpha(colorStyles.main, 0.12),
      },
    },
    text: {
      backgroundColor: "transparent",
      color: colorStyles.main,
      border: "none",
      boxShadow: "none",
      "&:hover": {
        backgroundColor:
          color === "subtle"
            ? alpha("#ffffff", 0.05)
            : alpha(colorStyles.main, 0.1),
        transform: "translateY(-1px)",
      },
      "&:active": {
        transform: "translateY(0)",
        backgroundColor:
          color === "subtle"
            ? alpha("#ffffff", 0.08)
            : alpha(colorStyles.main, 0.15),
      },
    },
  };

  // Calculate the final styles to apply
  const buttonStyles = {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "12px",
    textTransform: "none",
    fontWeight: 600,
    letterSpacing: "0.02em",
    lineHeight: 1.5,
    position: "relative",
    overflow: "hidden",
    transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
    ...sizeStyles[size],
    ...variantStyles[variant],
    "&:focus-visible": {
      outline: `2px solid ${colorStyles.main}`,
      outlineOffset: "2px",
    },
    "&:disabled": {
      opacity: 0.4,
      backgroundColor:
        variant === "contained" ? "rgba(255, 255, 255, 0.1)" : "transparent",
      color: "white",
      border:
        variant === "outlined"
          ? "1.5px solid rgba(255, 255, 255, 0.1)"
          : "none",
      cursor: "not-allowed",
      pointerEvents: "auto",
      transform: "none",
      boxShadow: "none",
      "&:hover": {
        backgroundColor:
          variant === "contained" ? "rgba(255, 255, 255, 0.08)" : "transparent",
        transform: "none",
        boxShadow: "none",
      },
    },
    // Ripple effect enhancement
    "& .MuiTouchRipple-root": {
      color:
        variant === "contained"
          ? alpha(colorStyles.contrastText, 0.3)
          : alpha(colorStyles.main, 0.3),
    },
    ...sx,
  };

  // Handle loading state
  const isDisabled = disabled || loading;
  const buttonStartIcon = loading ? null : startIcon;
  const buttonEndIcon = loading ? null : endIcon;
  const buttonChildren = loading ? loadingIndicator || "Loading..." : children;

  return (
    <Button
      variant={variant}
      color={color}
      onClick={onClick}
      disabled={isDisabled}
      fullWidth={fullWidth}
      size={size}
      startIcon={buttonStartIcon}
      endIcon={buttonEndIcon}
      aria-label={
        ariaLabel || (typeof children === "string" ? children : undefined)
      }
      aria-busy={loading}
      sx={buttonStyles}
      {...props}
    >
      {buttonChildren}
    </Button>
  );
};

// PropTypes for better documentation and development experience
ButtonAtom.propTypes = {
  variant: PropTypes.oneOf(["contained", "outlined", "text"]),
  color: PropTypes.string,
  onClick: PropTypes.func,
  disabled: PropTypes.bool,
  fullWidth: PropTypes.bool,
  loading: PropTypes.bool,
  size: PropTypes.oneOf(["small", "medium", "large"]),
  children: PropTypes.node.isRequired,
  startIcon: PropTypes.node,
  endIcon: PropTypes.node,
  loadingIndicator: PropTypes.node,
  ariaLabel: PropTypes.string,
  sx: PropTypes.object,
};

export default ButtonAtom;
